import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Gauge,
  Trophy,
  Palette,
  Coins,
  Apple,
  Play,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const FEATURES = [
  {
    icon: Gauge,
    title: "Buttery Physics Engine",
    desc: "Every flap, dip and rebound tuned frame-by-frame. The ball feels alive — because it basically is.",
    span: "md:col-span-2",
    id: "physics",
  },
  {
    icon: Trophy,
    title: "Global Leaderboards",
    desc: "Climb from Sunday League to World Champion. Your mates will know.",
    span: "",
    id: "leaderboards",
  },
  {
    icon: Palette,
    title: "Unlockable Ball Skins",
    desc: "Classic leather, neon streetball, retro '66 and 40+ more to earn.",
    span: "",
    id: "skins",
  },
  {
    icon: Coins,
    title: "Free To Play",
    desc: "No paywalls on the pitch. Skill is the only currency that matters.",
    span: "md:col-span-2",
    id: "free",
  },
];

export default function FlappyGoal() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <div data-testid="flappy-goal-page">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden grain pt-28 pb-16"
      >
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-pitch/80 via-pitch/60 to-pitch" />
          <div className="absolute inset-0 bg-gradient-to-tr from-emerald-600/20 via-lime-500/5 to-transparent" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-6"
              data-testid="hero-eyebrow"
            >
              Flappy Goal · Mobile Arcade Game — Season 2026
            </motion.p>

            <h1
              className="font-display leading-[0.88] tracking-tight text-[clamp(4.5rem,12vw,9.5rem)]"
              data-testid="hero-headline"
            >
              <Line delay={0.25}>FLAP.</Line>
              <Line delay={0.4}>
                <span className="text-volt drop-shadow-[0_0_24px_rgba(132,204,22,0.4)]">
                  SHOOT.
                </span>
              </Line>
              <Line delay={0.55}>
                <span className="text-outline">SCORE.</span>
              </Line>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="mt-7 max-w-md text-base sm:text-lg text-slate-300 leading-relaxed"
              data-testid="hero-subcopy"
            >
              Flappy Goal smashes one-tap flappy chaos into the beautiful game.
              Thread the ball through flying goal posts, ride the wind, and
              chase that impossible high score.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.7 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/download"
                data-testid="hero-download-cta"
                className="group inline-flex items-center gap-2.5 rounded-full bg-volt px-7 py-3.5 font-bold text-pitch transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                Download Free
                <ArrowRight
                  size={18}
                  strokeWidth={2.5}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-6 text-xs font-mono uppercase tracking-widest text-slate-500"
            >
              iOS & Android · Free to play · Offline friendly
            </motion.p>
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURES BENTO */}
      <section data-testid="features-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-4">
              What's in the game
            </p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none">
              BUILT FOR <span className="text-volt">ONE MORE GO</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.08} className={f.span}>
                <motion.div
                  data-testid={`feature-card-${f.id}`}
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  className="h-full rounded-2xl border border-line bg-turf/70 p-8 hover:border-volt/40 transition-colors duration-300"
                >
                  <span className="inline-flex w-12 h-12 rounded-xl bg-volt/10 border border-volt/20 items-center justify-center text-volt">
                    <f.icon size={22} />
                  </span>
                  <h3 className="mt-5 font-head text-xl sm:text-2xl font-bold">{f.title}</h3>
                  <p className="mt-2.5 text-sm sm:text-base text-slate-400 leading-relaxed">
                    {f.desc}
                  </p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section data-testid="download-cta-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-volt/25 bg-gradient-to-br from-turf via-pitch to-turf p-10 sm:p-16 text-center grain">
              <div
                className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-volt/10 blur-3xl"
                aria-hidden="true"
              />
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint">
                Kick-off is close
              </p>
              <h2 className="mt-4 font-display text-5xl sm:text-7xl tracking-tight leading-none">
                LACE UP. <span className="text-volt">FLAP HARD.</span>
              </h2>
              <p className="mt-5 max-w-lg mx-auto text-slate-300">
                Flappy Goal is landing on the App Store and Google Play. Grab it
                on launch day and start your streak before your friends do.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <Link
                  to="/download"
                  data-testid="cta-app-store-button"
                  className="inline-flex items-center gap-3 rounded-2xl bg-white text-pitch px-6 py-3 font-bold transition-transform duration-200 hover:scale-105 active:scale-95"
                >
                  <Apple size={22} />
                  <span className="text-left leading-tight">
                    <span className="block text-[10px] font-medium uppercase tracking-wide">
                      Download on the
                    </span>
                    App Store
                  </span>
                </Link>
                <Link
                  to="/download"
                  data-testid="cta-google-play-button"
                  className="inline-flex items-center gap-3 rounded-2xl border border-line bg-turf px-6 py-3 font-bold transition-transform duration-200 hover:scale-105 active:scale-95"
                >
                  <Play size={20} className="text-volt" fill="#84CC16" />
                  <span className="text-left leading-tight">
                    <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400">
                      Get it on
                    </span>
                    Google Play
                  </span>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
